import styled, {type Keyframes, keyframes} from 'styled-components';

import ImageWithAlt from '~/shared/components/ImageWithAlt';
import {media} from '~/shared/theme/media';

/* All of these are loaded as URLs rather than `?react` components: svgr runs svgo, whose
   preset-default drops a viewBox that matches the width/height attributes, and every one of these
   assets is authored that way. As <img> they are passed through untouched and keep the fractional
   intrinsic size the geometry below is measured against.

   The banknotes are assembled here from their layers rather than dropped in as one picture each,
   because Figma flattens the sheet's white fill into a single-image export of anything carrying a
   blur or a blend mode - which the two front notes both do. */
import shine from './assets/successShine.svg';
import stars from './assets/successStars.svg';
import note1Glow from './assets/successNote1Glow.svg';
import note1Note from './assets/successNote1Note.svg';
import note1Face from './assets/successNote1Face.svg';
import note1Speck1 from './assets/successNote1Speck1.svg';
import note1Speck2 from './assets/successNote1Speck2.svg';
import note1Icon from './assets/successNote1Icon.svg';
import note2Note from './assets/successNote2Note.svg';
import note2Speck1 from './assets/successNote2Speck1.svg';
import note2Speck2 from './assets/successNote2Speck2.svg';
import note2Sheen from './assets/successNote2Sheen.svg';
import note2Icon from './assets/successNote2Icon.svg';
import note3 from './assets/successNote3.svg';
import note4 from './assets/successNote4.svg';
import note5 from './assets/successNote5.svg';
import sparkleA from './assets/successSparkleA.svg';
import sparkleB from './assets/successSparkleB.svg';
import sparkleC from './assets/successSparkleC.svg';
import sparkleD from './assets/successSparkleD.svg';

/* The modal draws the same illustration as the sheet, spread across a wider frame at a slightly
   smaller size, so the size difference is a single factor per group and only the positions below are
   listed per breakpoint. The backdrop is scaled down further than the cash is. */
const WIDE_ART_SCALE = 0.96895;
const WIDE_BACKDROP_SCALE = 0.9188;

type Point = readonly [x: number, y: number];

/* Centres inside the illustration frame - 390x221 on the sheet, 500x163 in the modal. */
type Centers = {narrow: Point; wide: Point};

type Layer = {
  src: string;
  /* Centre of the layer inside its piece's box. Everything is placed by its centre because the
     turned layers are exported unturned, and CSS has to spin them about that same point to land them
     where the design has them. */
  at: Point;
  rotate?: number;
  mirrored?: boolean;
  /* Screen-blended against the layers underneath it, as the design has it. */
  screen?: boolean;
};

type Piece = {
  name: string;
  /* The box the layers sit in, and the box each breakpoint scales. */
  box: {width: number; height: number};
  center: Centers;
  /* The design crops this piece's artwork to its box. */
  cropped?: boolean;
  layers: readonly Layer[];
};

type Banknote = Piece & {
  /* Built by `noteFall`, and released `delay` after the screen appears. */
  fall: Keyframes;
  delay: number;
};

type Sparkle = Piece & {delay: number};

/* A note's entrance: it drops in from `from` above its resting place, unwinding `spin` on the way.
   Built once per note rather than from inside the styled component, so each note's keyframes are
   injected once instead of on every render. */
const noteFall = ({from, spin}: {from: number; spin: number}) => keyframes`
  from {
    transform: translate3d(0, ${from}px, 0) rotate(${spin}deg);
    opacity: 0;
  }

  35% {
    opacity: 1;
  }

  to {
    transform: translate3d(0, 0, 0) rotate(0deg);
    opacity: 1;
  }
`;

const sparkleTwinkle = keyframes`
  from {
    transform: scale(0.2);
    opacity: 0;
  }

  60% {
    transform: scale(1.3);
    opacity: 1;
  }

  to {
    transform: scale(1);
    opacity: 1;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const SHINE: Centers = {narrow: [194.5, -91], wide: [249.175, -83.209]};
const STARS: Centers = {narrow: [194, 83], wide: [248.716, 76.663]};

/* The cash itself, listed back to front to match the order the design stacks it in. Kept separate
   from the sparkles below because the two enter differently. */
const BANKNOTES: readonly Banknote[] = [
  {
    name: 'note5',
    box: {width: 97.615, height: 69.68},
    center: {narrow: [30.808, 85.284], wide: [63.701, 54.718]},
    layers: [{src: note5, at: [48.473, 34.691]}],
    fall: noteFall({from: -190, spin: -20}),
    delay: 0,
  },
  {
    name: 'note4',
    box: {width: 101.231, height: 77.824},
    center: {narrow: [356.385, 87.546], wide: [432.513, 56.909]},
    layers: [{src: note4, at: [50.856, 38.912]}],
    fall: noteFall({from: -200, spin: 26}),
    delay: 120,
  },
  {
    name: 'note3',
    box: {width: 76.639, height: 107.59},
    center: {narrow: [310.205, 22.365], wide: [364.056, -6.259]},
    layers: [{src: note3, at: [38.32, 53.795], rotate: 72.77}],
    fall: noteFall({from: -150, spin: -30}),
    delay: 200,
  },
  {
    name: 'note2',
    box: {width: 88, height: 60},
    center: {narrow: [136, 44], wide: [181.715, 14.709]},
    cropped: true,
    layers: [
      {src: note2Note, at: [43.836, 29.991]},
      {src: note2Speck1, at: [20.918, 35.691], rotate: -35.64},
      {src: note2Speck2, at: [54.173, 9.378], rotate: 9.15},
      {src: note2Sheen, at: [43.004, 26.779], screen: true},
      {src: note2Icon, at: [36.075, 19.703]},
    ],
    fall: noteFall({from: -180, spin: 18}),
    delay: 60,
  },
  {
    name: 'note1',
    box: {width: 105.75, height: 110.401},
    center: {narrow: [180.99, 157.225], wide: [236.315, 112.581]},
    layers: [
      {src: note1Glow, at: [47.079, 70.321]},
      {src: note1Note, at: [53.544, 50.45]},
      {src: note1Face, at: [54.431, 51.663]},
      {src: note1Speck1, at: [79.079, 33.327], rotate: 145.78},
      {src: note1Speck2, at: [33.926, 74.782], rotate: -169.36},
      {src: note1Icon, at: [59.676, 55.704]},
    ],
    fall: noteFall({from: -260, spin: -24}),
    delay: 160,
  },
];

/* These sit above the notes and land after them, so the last thing that moves is the glint. */
const SPARKLES: readonly Sparkle[] = [
  {
    name: 'sparkleA',
    box: {width: 9.038, height: 9.049},
    center: {narrow: [164.269, 64.923], wide: [220.114, 34.984]},
    layers: [{src: sparkleA, at: [4.519, 4.525]}],
    delay: 360,
  },
  {
    name: 'sparkleB',
    box: {width: 10.846, height: 11.764},
    center: {narrow: [306.173, 63.565], wide: [372.005, 33.671]},
    layers: [{src: sparkleB, at: [5.423, 5.882]}],
    delay: 480,
  },
  {
    name: 'sparkleC',
    box: {width: 10.204, height: 10.957},
    center: {narrow: [97.124, 136.934], wide: [155.053, 104.773]},
    layers: [{src: sparkleC, at: [5.102, 5.479], rotate: -25.74}],
    delay: 560,
  },
  {
    name: 'sparkleD',
    box: {width: 12.654, height: 15.384},
    center: {narrow: [253.75, 174.872], wide: [306.815, 129.683]},
    layers: [{src: sparkleD, at: [6.327, 7.692], mirrored: true}],
    delay: 420,
  },
];

/* Where a piece sits at each breakpoint. */
type CenterProps = {$x: number; $y: number; $wideX: number; $wideY: number};

/* Crops the illustration to the strip the design reveals. The notes start their drop above this box,
   so they are hidden until they enter it rather than appearing over the text below. */
const Root = styled.div`
  position: relative;
  flex: none;
  height: 221px;
  overflow: hidden;
  pointer-events: none;
  ${media.minLargeMobile`
    height: 163px;
  `}
`;

/* The frame every position above is measured in. It is wider than the sheet on a phone - the design
   lets the outer notes run off both edges - so it is centred rather than stretched. Centred with a
   margin and not a transform on purpose: a transform here would open a stacking context and cut the
   screen-blended burst off from the gradient it is supposed to lighten. */
const Canvas = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  width: 390px;
  height: 100%;
  margin-left: -195px;
  ${media.minLargeMobile`
    width: 500px;
    margin-left: -250px;
  `}
`;

const Backdrop = styled(ImageWithAlt)<CenterProps>`
  position: absolute;
  top: ${({$y}) => $y}px;
  left: ${({$x}) => $x}px;
  transform: translate(-50%, -50%);
  animation: ${fadeIn} 600ms ease-out both;
  ${({$wideX, $wideY}) => media.minLargeMobile`
    top: ${$wideY}px;
    left: ${$wideX}px;
    transform: translate(-50%, -50%) scale(${WIDE_BACKDROP_SCALE});
  `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  body.contrast-filter & {
    display: none;
  }
`;

/* Screen blending is what makes the burst lighten the gradient behind it instead of washing it out.
   Its own 30% opacity is already baked into the asset. */
const Shine = styled(Backdrop)`
  mix-blend-mode: screen;
`;

const Piece = styled.div<CenterProps & {$width: number; $height: number}>`
  position: absolute;
  top: ${({$y}) => $y}px;
  left: ${({$x}) => $x}px;
  width: ${({$width}) => $width}px;
  height: ${({$height}) => $height}px;
  transform: translate(-50%, -50%);
  ${({$wideX, $wideY}) => media.minLargeMobile`
    top: ${$wideY}px;
    left: ${$wideX}px;
    transform: translate(-50%, -50%) scale(${WIDE_ART_SCALE});
  `}
`;

/* The part that actually moves, so the piece's resting place and its entrance stay independent.
   Isolated so that the screen-blended sheen on one of the notes lightens that note and nothing else. */
const PieceBody = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  isolation: isolate;
`;

const FallingPiece = styled(PieceBody)<{$fall: Keyframes; $delay: number; $cropped?: boolean}>`
  overflow: ${({$cropped}) => ($cropped ? 'hidden' : 'visible')};
  animation: ${({$fall}) => $fall} 1000ms cubic-bezier(0.22, 1, 0.36, 1) ${({$delay}) => $delay}ms both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const TwinklingPiece = styled(PieceBody)<{$delay: number}>`
  animation: ${sparkleTwinkle} 520ms ease-out ${({$delay}) => $delay}ms both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const LayerArt = styled(ImageWithAlt)<{
  $x: number;
  $y: number;
  $rotate: number;
  $flip: number;
  $screen?: boolean;
}>`
  position: absolute;
  top: ${({$y}) => $y}px;
  left: ${({$x}) => $x}px;
  transform: translate(-50%, -50%) rotate(${({$rotate}) => $rotate}deg) scaleX(${({$flip}) => $flip});
  ${({$screen}) => $screen && 'mix-blend-mode: screen;'}

  body.contrast-filter & {
    display: none;
  }
`;

const centerProps = ({narrow, wide}: Centers): CenterProps => ({
  $x: narrow[0],
  $y: narrow[1],
  $wideX: wide[0],
  $wideY: wide[1],
});

/* Keyed by position rather than by `src`: a piece's layers are a fixed list that is never reordered,
   and the same asset can legitimately appear in it twice. */
const renderLayers = ({layers}: Piece) =>
  layers.map(({src, at, rotate, mirrored, screen}, index) => (
    <LayerArt
      // eslint-disable-next-line react/no-array-index-key
      key={index}
      src={src}
      $x={at[0]}
      $y={at[1]}
      $rotate={rotate ?? 0}
      $flip={mirrored ? -1 : 1}
      $screen={screen}
      noAlt
    />
  ));

/* The header of the success screen: a burst of cash that drops in when the screen first appears, over
   the glow and star field the modal's gradient runs out of. Purely decorative - what was converted is
   announced by the text underneath. */
const ConvertLoyaltyBalanceSuccessIllustration = () => (
  <Root aria-hidden="true">
    <Canvas>
      <Shine src={shine} {...centerProps(SHINE)} noAlt />
      <Backdrop src={stars} {...centerProps(STARS)} noAlt />
      {BANKNOTES.map(piece => (
        <Piece
          key={piece.name}
          {...centerProps(piece.center)}
          $width={piece.box.width}
          $height={piece.box.height}
        >
          <FallingPiece $fall={piece.fall} $delay={piece.delay} $cropped={piece.cropped}>
            {renderLayers(piece)}
          </FallingPiece>
        </Piece>
      ))}
      {SPARKLES.map(piece => (
        <Piece
          key={piece.name}
          {...centerProps(piece.center)}
          $width={piece.box.width}
          $height={piece.box.height}
        >
          <TwinklingPiece $delay={piece.delay}>{renderLayers(piece)}</TwinklingPiece>
        </Piece>
      ))}
    </Canvas>
  </Root>
);

export default ConvertLoyaltyBalanceSuccessIllustration;
